import { useMediaQuery } from '@mui/material';
import Hls from 'hls.js';
import { FC, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import dunbarWhite from 'src/assets/images/dunbar/dunbar-white.png';
import { VideoItemProps } from 'src/pages/UserCircle/const';
import { useVideoEngagement } from 'src/utils/videoEngagement';
import { SelectedVideoModal } from '../SelectedVideoModal/SelectedVideoModal';
import styles from './VideoItem.module.scss';

export const VideoItem: FC<VideoItemProps> = ({ item }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useMediaQuery('(max-width:545px)');
  const location = useLocation();
  const navigate = useNavigate();
  const [currentPlayingVideo, setCurrentPlayingVideo] =
    useState<HTMLVideoElement | null>(null);

  const { createHandlers } = useVideoEngagement({
    postId: item._id || '',
  });

  // Video Loading & HLS Setup
  useEffect(() => {
    let hls: Hls | undefined;
    const video = videoRef.current;

    const handleCanPlay = () => setIsLoading(false);
    const handleWaiting = () => setIsLoading(true);
    const handleLoadedData = () => setIsLoading(false);

    if (!video || !item.video_url) {
      return;
    }

    video.oncanplay = handleCanPlay;
    video.onwaiting = handleWaiting;
    video.onloadeddata = handleLoadedData;

    if (Hls.isSupported()) {
      hls = new Hls();
      hls.loadSource(item.video_url);
      hls.attachMedia(video);
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = item.video_url;
    }

    return () => {
      if (video) {
        video.oncanplay = null;
        video.onwaiting = null;
        video.onloadeddata = null;
      }
      hls?.destroy();
    };
  }, [item.video_url]);

  // Set up video engagement handlers
  useEffect(() => {
    if (item && videoRef.current) {
      const { handleTimeUpdate, handleEnded } = createHandlers(videoRef);
      const video = videoRef.current;

      video.addEventListener('timeupdate', handleTimeUpdate);
      video.addEventListener('ended', handleEnded);

      return () => {
        video.removeEventListener('timeupdate', handleTimeUpdate);
        video.removeEventListener('ended', handleEnded);
      };
    }
  }, [item, createHandlers]);

  const handleVideoClick = () => {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    if (video.paused) {
      video.play().catch((err) => console.error('Playback error:', err));
    } else {
      video.pause();
    }
  };

  const handleDunbarClick = () => {
    const circleId = location.pathname.split('/')[2];
    navigate(`/circle/${circleId}/challenge/${item._id}`);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const truncateText = (text: string, maxLength: number) =>
    text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;

  const calculateRelativeTime = (date: string) => {
    const createdAt = new Date(date);
    return `${createdAt.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
    })}, ${createdAt.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })}`;
  };

  const displayedChallenges = item.challenges.slice(0, 2);
  const moreChallengesCount =
    item.challenges.length - displayedChallenges.length;
  const relativeTime = calculateRelativeTime(item.createdAt);

  const handleMouseEnter = () => {
    const current = videoRef.current;

    if (!isMobile && current) {
      if (currentPlayingVideo && currentPlayingVideo !== current) {
        currentPlayingVideo.pause();
      }

      if (current.readyState >= 3) {
        current
          .play()
          .then(() => {
            setCurrentPlayingVideo(current);
          })
          .catch((err) => console.error('Play error:', err));
      } else {
        const onCanPlay = () => {
          current
            .play()
            .then(() => {
              setCurrentPlayingVideo(current);
            })
            .catch((err) => console.error('Play error:', err));
          current.oncanplay = null;
        };
        current.oncanplay = onCanPlay;
      }
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile && videoRef.current) {
      videoRef.current.pause();
    }
  };

  return (
    <div
      className={styles.videoContainer}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles.videoWrapper}>
        {isLoading && item.video_url && (
          <div className={styles.loaderOverlay}>
            <div className={styles.loaderSpinner}></div>
          </div>
        )}

        {item.video_url ? (
          <video
            ref={videoRef}
            onClick={handleVideoClick}
            className={styles.video}
            playsInline
          >
            Your browser does not support the video tag.
          </video>
        ) : (
          <div className={styles.videoUnavailable}>Video unavailable</div>
        )}
      </div>

      <div className={styles.challengeOverlay}>
        <div className={styles.topData}>
          <div className={styles.circleName}>{item.tribe_name}</div>
          <div className={styles.time}>{relativeTime}</div>
        </div>
        <div className={styles.bottomTitle}>Actions:</div>
        {displayedChallenges.map((challenge, index) => (
          <div className={styles.challengeItem} key={index}>
            <div className={styles.challengeTitle}>
              {truncateText(challenge.name, 37)}
            </div>
          </div>
        ))}
        {moreChallengesCount > 0 && (
          <div className={styles.moreItem}>+{moreChallengesCount} more</div>
        )}
        <div className={styles.dunbarIconWrapper} onClick={handleDunbarClick}>
          <img src={dunbarWhite} alt="Dunbar" className={styles.dunbarIcon} />
        </div>
        <div className={styles.commentsCount} onClick={handleDunbarClick}>
          {item.comments.length} comments
        </div>
      </div>

      <SelectedVideoModal
        isOpenModal={isOpenModal}
        setIsOpenModal={setIsOpenModal}
        postId={item._id}
        videoUrl={item.video_url}
      />
    </div>
  );
};
